/** UploadDropzone — 3 files (zip, manifest, signature). */
import React, { useCallback, useRef, useState } from "react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { Alert, AlertDescription } from "../ui/alert";
import { UploadCloud, FileArchive, FileJson, FileSignature, X } from "lucide-react";
import { useUpdater } from "../../hooks/useUpdater";

const DEFAULT_MAX_MB = 500;

export default function UploadDropzone({ maxUploadMb = DEFAULT_MAX_MB, onUploaded }) {
  const { uploadPackage, extractDetail } = useUpdater();
  const [files, setFiles] = useState({ zip: null, manifest: null, signature: null });
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const zipRef = useRef(null);
  const manifestRef = useRef(null);
  const sigRef = useRef(null);

  const bytesLimit = maxUploadMb * 1024 * 1024;

  const setFile = (kind, f) => {
    setError(null);
    if (!f) { setFiles((s) => ({ ...s, [kind]: null })); return; }
    if (f.size > bytesLimit) {
      setError(`Fichier trop volumineux (${(f.size / 1024 / 1024).toFixed(1)} MB > ${maxUploadMb} MB).`);
      return;
    }
    setFiles((s) => ({ ...s, [kind]: f }));
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files || []);
    const next = { zip: null, manifest: null, signature: null };
    for (const f of dropped) {
      if (f.name.endsWith(".zip")) next.zip = f;
      else if (f.name === "manifest.json") next.manifest = f;
      else if (f.name === "signature.sig") next.signature = f;
    }
    setFiles((prev) => ({ ...prev, ...next }));
  }, []);

  const ready = files.zip && files.manifest && files.signature;

  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setError(null); setProgress(0);
    try {
      const data = await uploadPackage({
        zipFile: files.zip, manifestFile: files.manifest, signatureFile: files.signature,
        onProgress: (p) => setProgress(p),
      });
      onUploaded?.(data);
      setFiles({ zip: null, manifest: null, signature: null });
      setProgress(0);
    } catch (err) {
      setError(extractDetail(err, "Échec de l'upload."));
    } finally {
      setBusy(false);
    }
  };

  const Item = ({ kind, label, icon: Icon, inputRef, accept }) => (
    <div className="flex items-center gap-2 border border-border rounded-md p-2" data-testid={`file-${kind}`}>
      <Icon className="h-5 w-5 text-primary shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm truncate">{files[kind]?.name || "—"}</p>
      </div>
      {files[kind] && (
        <button type="button" onClick={() => setFile(kind, null)} className="text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>
      )}
      <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()}>
        Choisir
      </Button>
      <input ref={inputRef} type="file" accept={accept} hidden onChange={(e) => setFile(kind, e.target.files?.[0])} />
    </div>
  );

  return (
    <Card className="p-6 space-y-4">
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className="border-2 border-dashed border-border rounded-md p-6 text-center bg-secondary/40"
        data-testid="upload-dropzone"
      >
        <UploadCloud className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">
          Glissez-déposez les 3 fichiers du release (<code>*.zip</code>, <code>manifest.json</code>, <code>signature.sig</code>) ou sélectionnez-les individuellement ci-dessous.
        </p>
        <p className="text-xs text-muted-foreground mt-1">Taille max : {maxUploadMb} MB par fichier.</p>
      </div>
      <div className="grid grid-cols-1 gap-2">
        <Item kind="zip" label="Package (.zip)" icon={FileArchive} inputRef={zipRef} accept=".zip" />
        <Item kind="manifest" label="manifest.json" icon={FileJson} inputRef={manifestRef} accept="application/json,.json" />
        <Item kind="signature" label="signature.sig" icon={FileSignature} inputRef={sigRef} accept=".sig" />
      </div>
      {busy && <Progress value={progress} data-testid="upload-progress" />}
      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
      <div className="flex justify-end">
        <Button disabled={!ready || busy} onClick={submit} data-testid="btn-upload">
          {busy ? "Envoi…" : "Envoyer le package"}
        </Button>
      </div>
    </Card>
  );
}
